import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  Bot,
  Cpu,
  FileUp,
  LoaderCircle,
  MessageSquarePlus,
  Paperclip,
  Sparkles,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, streamApi } from '../lib/api';

interface ChatSummary {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: string;
  mode: { key: string; nameAr: string };
  _count: { messages: number };
}

interface ChatDetail extends Omit<ChatSummary, '_count'> {
  messages: Array<{
    id: string;
    role: 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL';
    content: string;
    createdAt: string;
    providerKind?: string;
    modelId?: string;
  }>;
  attachments: Array<{
    id: string;
    originalName: string;
    sizeBytes: number;
    mimeType: string;
  }>;
}

const quickPrompts = ['لخص مستند المشروع', 'استخرج بيانات لوحة الرسم', 'اقترح خطة عمل للمشروع'];

export function AssistantPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const chatsQuery = useQuery({
    queryKey: ['chats'],
    queryFn: () => api<ChatSummary[]>('/api/chats'),
  });

  useEffect(() => {
    if (!selectedId && chatsQuery.data?.[0]) setSelectedId(chatsQuery.data[0].id);
  }, [selectedId, chatsQuery.data]);

  const chatQuery = useQuery({
    queryKey: ['chat', selectedId],
    queryFn: () => api<ChatDetail>(`/api/chats/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [chatQuery.data?.messages.length, streamingText]);

  const createChat = useMutation({
    mutationFn: () =>
      api<ChatSummary>('/api/chats', {
        method: 'POST',
        body: JSON.stringify({
          title: `محادثة ${new Intl.DateTimeFormat('ar-SA', { dateStyle: 'short', timeStyle: 'short' }).format(new Date())}`,
          modeKey: 'general_assistant',
        }),
      }),
    onSuccess: async (chat) => {
      setSelectedId(chat.id);
      await queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onError: (error) => toast.error(error.message),
  });

  async function sendMessage() {
    const content = message.trim();
    if (!content || !selectedId || sending) return;
    setMessage('');
    setSending(true);
    setStreamingText('');
    try {
      await streamApi(`/api/chats/${selectedId}/messages/stream`, {
        content,
        taskProfile: 'BALANCED',
      }, {
        onToken: (token) => setStreamingText((current) => current + token),
        onDone: () => undefined,
        onError: (failure) => toast.error(failure),
      });
      await queryClient.invalidateQueries({ queryKey: ['chat', selectedId] });
      await queryClient.invalidateQueries({ queryKey: ['chats'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر إرسال الرسالة');
    } finally {
      setSending(false);
      setStreamingText('');
    }
  }

  async function uploadFile(file: File | undefined) {
    if (!file || !selectedId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('chatId', selectedId);
      await api('/api/files/upload', { method: 'POST', body: form });
      toast.success('تم رفع الملف للمعالجة المؤقتة');
      await queryClient.invalidateQueries({ queryKey: ['chat', selectedId] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر رفع الملف');
    } finally {
      setUploading(false);
    }
  }

  async function processAttachment(attachmentId: string) {
    if (!selectedId) return;
    try {
      await api('/api/jobs', {
        method: 'POST',
        body: JSON.stringify({
          type: 'PROCESS_ATTACHMENT',
          chatId: selectedId,
          attachmentId,
          input: { source: 'assistant_attachment' },
        }),
      });
      toast.success('أُضيفت المهمة إلى طابور المعالجة');
      navigate('/app/jobs');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر بدء المعالجة');
    }
  }

  const chat = chatQuery.data;
  return (
    <div className="grid min-h-screen lg:h-screen lg:grid-cols-[280px_1fr]">
      <aside className="hidden border-l border-ink/10 bg-white/60 p-4 lg:flex lg:flex-col">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold text-slate-400">مساحة المساعد</p>
            <h1 className="mt-1 text-lg font-black">المحادثات</h1>
          </div>
          <button
            type="button"
            onClick={() => createChat.mutate()}
            disabled={createChat.isPending}
            className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-white transition hover:bg-teal"
            aria-label="محادثة جديدة"
          >
            <MessageSquarePlus className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto">
          {chatsQuery.data?.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className={`w-full rounded-xl px-3 py-3 text-start transition ${
                item.id === selectedId ? 'bg-ink text-white' : 'hover:bg-white'
              }`}
            >
              <p className="truncate text-xs font-black">{item.title}</p>
              <p className={`mt-1 text-[10px] ${item.id === selectedId ? 'text-white/45' : 'text-slate-400'}`}>
                {item.mode.nameAr} · {item._count.messages} رسالة
              </p>
            </button>
          ))}
          {!chatsQuery.isLoading && chatsQuery.data?.length === 0 && (
            <div className="rounded-2xl border border-dashed border-ink/15 p-4 text-center text-[11px] font-bold text-slate-500">
              لا توجد محادثات بعد
            </div>
          )}
        </div>
      </aside>

      <section className="flex min-h-screen flex-col lg:min-h-0">
        <header className="flex h-20 items-center justify-between border-b border-ink/10 bg-white/60 px-4 sm:px-7">
          <div>
            <p className="text-[10px] font-bold text-teal">المساعد العام</p>
            <h2 className="mt-1 text-base font-black">{chat?.title || 'مرحبًا بك في Details AI'}</h2>
          </div>
          <button
            type="button"
            onClick={() => createChat.mutate()}
            className="inline-flex items-center gap-2 rounded-xl border border-ink/10 bg-white px-3 py-2 text-[11px] font-black lg:hidden"
          >
            <MessageSquarePlus className="h-4 w-4" />
            جديدة
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          {!chat && !chatQuery.isLoading ? (
            <div className="mx-auto flex max-w-2xl flex-col items-center pt-16 text-center sm:pt-24">
              <div className="mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-ink text-white shadow-xl shadow-ink/15">
                <Bot className="h-7 w-7" />
              </div>
              <h2 className="text-2xl font-black">كيف أساعدك اليوم؟</h2>
              <p className="mt-3 max-w-lg text-sm font-semibold leading-7 text-slate-500">
                ابدأ محادثة للمشروع، أو ارفع ملفًا لمعالجته محليًا. لن يظهر أي رد
                اصطناعي حتى يتم إعداد مفتاح مزود من لوحة الإدارة.
              </p>
              <button
                type="button"
                onClick={() => createChat.mutate()}
                className="mt-7 inline-flex items-center gap-2 rounded-xl bg-teal px-5 py-3 text-sm font-black text-white"
              >
                <MessageSquarePlus className="h-4 w-4" />
                ابدأ محادثة
              </button>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-5">
              {chat?.messages.length === 0 && (
                <div className="mb-8 rounded-2xl bg-gradient-to-l from-ink to-teal p-6 text-white shadow-panel">
                  <Sparkles className="mb-4 h-6 w-6 text-gold" />
                  <h3 className="text-lg font-black">مساحة سياق آمنة</h3>
                  <p className="mt-2 max-w-xl text-xs font-semibold leading-6 text-white/65">
                    اكتب طلبك أو ارفع ملفًا. يتم بناء السياق محليًا قبل إرسال الجزء
                    اللازم فقط إلى المزود المصرح به.
                  </p>
                </div>
              )}
              {chat?.messages.map((item) => (
                <article
                  key={item.id}
                  className={`flex gap-3 ${item.role === 'USER' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                      item.role === 'USER' ? 'bg-gold/20 text-gold' : 'bg-ink text-white'
                    }`}
                  >
                    {item.role === 'USER' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm font-semibold leading-7 ${
                      item.role === 'USER'
                        ? 'bg-ink text-white'
                        : 'border border-ink/10 bg-white text-slate-700 shadow-sm'
                    }`}
                  >
                    {item.content}
                    {item.modelId && (
                      <p className="mt-2 text-[9px] font-bold opacity-45">
                        {item.providerKind} · {item.modelId}
                      </p>
                    )}
                  </div>
                </article>
              ))}
              {streamingText && (
                <article className="flex gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-ink text-white">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl border border-ink/10 bg-white px-4 py-3 text-sm font-semibold leading-7 text-slate-700 shadow-sm">
                    {streamingText}
                    <span className="ms-1 inline-block h-4 w-1 animate-pulse bg-teal align-middle" />
                  </div>
                </article>
              )}
            </div>
          )}
        </div>

        <footer className="border-t border-ink/10 bg-white/70 p-4 sm:p-5">
          <div className="mx-auto max-w-3xl">
            {chat?.attachments && chat.attachments.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {chat.attachments.map((file) => (
                  <span
                    key={file.id}
                    className="inline-flex items-center gap-2 rounded-lg border border-ink/10 bg-white px-2.5 py-1.5 text-[10px] font-bold text-slate-600"
                  >
                    <Paperclip className="h-3 w-3 text-teal" />
                    {file.originalName}
                    <button
                      type="button"
                      onClick={() => void processAttachment(file.id)}
                      className="ms-1 inline-flex items-center gap-1 rounded-md bg-teal/10 px-1.5 py-0.5 text-[9px] font-black text-teal hover:bg-teal/20"
                    >
                      <Cpu className="h-2.5 w-2.5" />
                      معالجة محلية
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2 rounded-2xl border border-ink/15 bg-white p-2 shadow-lg shadow-ink/5 focus-within:border-teal/50">
              <label className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-xl text-slate-500 transition hover:bg-teal/10 hover:text-teal">
                {uploading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                <input
                  type="file"
                  className="hidden"
                  disabled={!selectedId || uploading}
                  onChange={(event) => void uploadFile(event.target.files?.[0])}
                />
              </label>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void sendMessage();
                  }
                }}
                rows={1}
                disabled={!selectedId || sending}
                placeholder={selectedId ? 'اكتب طلبك…' : 'أنشئ محادثة للبدء'}
                className="max-h-36 min-h-10 flex-1 resize-none bg-transparent px-2 py-2 text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => void sendMessage()}
                disabled={!message.trim() || !selectedId || sending}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ink text-white transition hover:bg-teal disabled:opacity-30"
                aria-label="إرسال"
              >
                {sending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </button>
            </div>
            {chat?.messages.length === 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {quickPrompts.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => setMessage(prompt)}
                    className="rounded-lg border border-ink/10 bg-white px-3 py-1.5 text-[10px] font-bold text-slate-500 transition hover:border-teal/30 hover:text-teal"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-2 text-center text-[9px] font-bold text-slate-400">
              لا ترسل أسرارًا أو كلمات مرور. الملفات المرفوعة مؤقتة وتُحذف تلقائيًا.
            </p>
          </div>
        </footer>
      </section>
    </div>
  );
}
